import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';

const DEFAULTS = {
  logo: '/definitelogo.png',
  sidebarColor: '#1a5c5a',
  theme: 'light',
};

const ThemeConfigContext = createContext({
  config: DEFAULTS,
  loading: true,
  agentCode: '',
  saveConfig: async () => ({ success: false }),
});

// Resolves the signed-in agent (from the same localStorage/credentials flow every page
// already uses), then loads and applies their saved branding/theme config once for the
// whole app - instead of every page independently fetching + applying it.
export function ThemeConfigProvider({ children }) {
  const [credentials, setCredentials] = useState(null);
  const [agentCode, setAgentCode] = useState('');
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
      } catch {
        // No credentials (e.g. still on the login page) - stay on defaults.
      }
    })();
  }, []);

  useEffect(() => {
    if (!credentials) return;
    const email = localStorage.getItem('definiteagent');
    if (!email) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${email}`, {
          headers: { Authorization: `Basic ${credentials}` },
        });
        const dealer = await res.json();
        if (dealer && dealer.OID) setAgentCode(dealer.OID);
        else setLoading(false);
      } catch {
        setLoading(false);
      }
    })();
  }, [credentials]);

  const fetchConfig = useCallback(async () => {
    if (!agentCode) return;
    try {
      const res = await fetch(`${API}/getUserConfig?agent=${agentCode}`);
      const data = await res.json();
      setConfig({ ...DEFAULTS, ...(data.config || {}) });
    } catch {
      setConfig(DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, [agentCode]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // Apply theme + sidebar color to the document root so every page's CSS (via
  // var(--bg), var(--sidebar-bg), etc. from theme.css) picks it up automatically.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', config.theme === 'dark' ? 'dark' : 'light');
    document.documentElement.style.setProperty('--sidebar-bg', config.sidebarColor || DEFAULTS.sidebarColor);
  }, [config]);

  // sidebarColor/theme are plain strings; logoFile (optional) is a File from an
  // <input type="file">. Sends whichever of the three are provided; the server merges
  // them into the agent's existing saved config rather than overwriting the whole thing.
  const saveConfig = useCallback(async ({ sidebarColor, theme, logoFile } = {}) => {
    if (!agentCode) return { success: false, message: 'No agent to save settings for' };
    const formData = new FormData();
    formData.append('agent', agentCode);
    if (sidebarColor) formData.append('sidebarColor', sidebarColor);
    if (theme) formData.append('theme', theme);
    if (logoFile) formData.append('logo', logoFile);

    try {
      const res = await fetch(`${API}/updateUserConfig`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) setConfig((prev) => ({ ...prev, ...data.config }));
      return data;
    } catch {
      return { success: false, message: 'Network error' };
    }
  }, [agentCode]);

  return (
    <ThemeConfigContext.Provider value={{ config, loading, agentCode, saveConfig }}>
      {children}
    </ThemeConfigContext.Provider>
  );
}

export const useThemeConfig = () => useContext(ThemeConfigContext);
